import SuperFetch, { AbortSuperFetch } from '../helpers/superFetch';
import { UniqueData } from '../redux/marks/types';
import {
  SignMarkDataPackage,
  SignMarkDataPackageResponse,
  SignMarkGroupDataPackage,
  SignMarkGroupDataPackageResponse,
  SmsCodeData,
  SmsCodeDataResponse,
  EmailCodeData,
  EmailCodeDataResponse,
  SignMarkData,
  SignMarkDataResponse,
  SignMarksGroupData,
  EmployeeChooseData,
  EmployeeChooseDataResponse,
  EmployeeEmailCodeData,
  EmployeeEmailCodeDataResponse,
  CopyFinalMarkDataRequestPayload,
} from './types/marks';

export interface ErrorRequest {
  errorCode: string;
  status: string;
}

type CreateUpdateDeleteFinalMark = (data: UniqueData) => Promise<Response>;
type CreateOrUpdateFinalMarks = (data: UniqueData[]) => Promise<Response>;
type CreateUpdateDeletePartialMark = (data: UniqueData) => Promise<Response>;
type CreateUpdateDeletePartialMarks = (data: UniqueData[]) => Promise<Response>;
type SignMarkFirstStep = (data: SignMarkDataPackage, signal: AbortSignal) => Promise<SignMarkDataPackageResponse>;
type SignMarksFirstStep = (data: SignMarkGroupDataPackage, signal: AbortSignal) => Promise<SignMarkGroupDataPackageResponse | { error: boolean }>;
type SignMarksSmsCode = (data: SmsCodeData) => Promise<unknown>;
type SignMarksEmailCode = (data: EmailCodeData) => Promise<unknown>;
type SignMarkConfirm = (data: SignMarkData) => Promise<unknown>;
type SignMarksConfirm = (data: SignMarksGroupData) => Promise<unknown>;
type SignMarksChooseEmployee = (data: EmployeeChooseData) => Promise<EmployeeChooseDataResponse>;
type SignMarksCodeEmployee = (data: EmployeeEmailCodeData) => Promise<EmployeeEmailCodeDataResponse>;
type CopyFinalMarksBasedOnCalculatedMarks = (data: CopyFinalMarkDataRequestPayload, signal: AbortSignal) => Promise<Response>;

/** 
 * Klasa zawiera metody służące dodawaniu bądź edycji ocen
 * 
 */
class Marks {
  /** Tworzenie oceny końcowej */
  createFinalMark: CreateUpdateDeleteFinalMark = (data: UniqueData) => {
    return SuperFetch.post(`oceny/ocena-koncowa-pojedyncza`, data);
  };
  /** Edycja oceny końcowej */
  updateFinalMark: CreateUpdateDeleteFinalMark = (data: UniqueData) => {
    return SuperFetch.put(`oceny/ocena-koncowa-pojedyncza`, data);
  };
  /** Usuwanie oceny końcowej */
  deleteFinalMark: CreateUpdateDeletePartialMark = data => {
    return SuperFetch.delete(`oceny/ocena-koncowa-pojedyncza`, data);
  };
  /** Tworzenie wielu ocen końcowych */
  createFinalMarks: CreateOrUpdateFinalMarks = (data: UniqueData[]) => {
    return SuperFetch.post(`oceny/ocena-koncowa-grupowa`, { data });
  };
  /** Edycja wielu ocen końcowych */
  updateFinalMarks: CreateOrUpdateFinalMarks = (data: UniqueData[]) => {
    return SuperFetch.put(`oceny/ocena-koncowa-grupowa`, { data });
  };
  /** Tworzenie oceny cząstkowej */
  createPartialMark: CreateUpdateDeletePartialMark = (data: UniqueData) => {
    return SuperFetch.post(`oceny/ocena-czastkowa-pojedyncza`, data);
  };
  /** Edycja oceny cząstkowej */
  updatePartialMark: CreateUpdateDeletePartialMark = (data: UniqueData) => {
    return SuperFetch.put(`oceny/ocena-czastkowa-pojedyncza`, data);
  };
  /** Tworzenie wielu ocen cząstkowych */
  createPartialMarks: CreateUpdateDeletePartialMarks = (data: UniqueData[]) => {
    return SuperFetch.post(`oceny/ocena-czastkowa-grupowa`, { data });
  };
  /** Edycja wielu ocen cząstkowych */
  updatePartialMarks: CreateUpdateDeletePartialMarks = (data: UniqueData[]) => {
    return SuperFetch.put(`oceny/ocena-czastkowa-grupowa`, { data });
  };
  /** Usuwanie oceny cząstkowej */
  deletePartialMark: CreateUpdateDeletePartialMark = (data: UniqueData) => {
    return SuperFetch.delete(`oceny/ocena-czastkowa-pojedyncza`, data);
  };
  /** Tworzenie wielu ocen cząstkowych */
  deletePartialMarks: CreateUpdateDeletePartialMarks = (data: UniqueData[]) => {
    return SuperFetch.delete(`oceny/ocena-czastkowa-grupowa`, { data });
  };
  /** Krok 1 podpisywania oceny */
  signMarkFirstStep: SignMarkFirstStep = async (data, signal) => {
    const response = await AbortSuperFetch.post(`oceny/podpisz/rozpocznij`, signal, data) as unknown;
    return response as SignMarkDataPackageResponse | ErrorRequest | any;
  };
  /** Krok 1 podpisywania ocen grupowo */
  signMarksFirstStep: SignMarksFirstStep = async (data, signal) => {
    const response = await AbortSuperFetch.post(`oceny/podpisz/rozpocznij-grupowo`, signal, data) as unknown;
    return response as SignMarkGroupDataPackageResponse | ErrorRequest | any;
  };
  /** 2Fa - kod SMS */
  signMarksSmsCode: SignMarksSmsCode = async (data) => {
    const response = await SuperFetch.post(`oceny/podpisz/kod-sms`, data) as unknown;
    return response as SmsCodeDataResponse | ErrorRequest | any;
  };
  /** 2Fa - kod Email */
  signMarksEmailCode: SignMarksEmailCode = async (data) => {
    const response = await SuperFetch.post(`oceny/podpisz/kod-email`, data) as unknown;
    return response as EmailCodeDataResponse | ErrorRequest | any;
  };
  /** Podpisywanie oceny - krok potwierdzający */
  signMarkConfirm: SignMarkConfirm = async (data) => {
    const response = await SuperFetch.post(`oceny/podpisz/podpisz-ocene`, data) as unknown;
    return response as SignMarkDataResponse | ErrorRequest | any;
  };
  /** Podpisywanie ocen grupowo - krok potwierdzający */
  signMarksConfirm: any = async (data: any) => {
    const response = await SuperFetch.post(`oceny/podpisz/podpisz-ocene-grupowo`, data) as unknown;
    return response as SignMarkDataResponse | ErrorRequest | any;
  };
  /** Podpisywanie ocen złożone - wybór pracownika weryfikującego */
  signMarksChooseEmployee: SignMarksChooseEmployee = async (data) => {
    const response = await SuperFetch.post(`oceny/podpisz/rozpocznij-weryfikacje`, data) as unknown;
    return response as EmployeeChooseDataResponse | ErrorRequest | any;
  };
  /** Podpisywanie ocen złożone - wysyładnie kodu pracownika */
  signMarksCodeEmployee: SignMarksCodeEmployee = async (data) => {
    const response = await SuperFetch.post(`oceny/podpisz/kod-email-osoby-weryfikujacej`, data) as unknown;
    return response as EmployeeEmailCodeDataResponse | ErrorRequest | any;
  };
  /** Przepisanie ocen końcowych na podstawie wyliczoncych */
  copyFinalMarksBasedOnCalculatedMarks: CopyFinalMarksBasedOnCalculatedMarks = async (data, signal) => {
    const response = await AbortSuperFetch.post(`oceny/ocena-wyliczona-grupowa-przepisz`, signal, data);
    return response;
  };
}

export default new Marks();